/**
 * Login Screen Component
 * Routing rules:
 *  - the player has left a session the server ended, or the tab remembers the
 *    end of the session its link names → SessionEndedEntry, which joins
 *    nothing (before every mode below, each of which starts a join)
 *  - URL has ?mode=admin-launch         → AdminLaunchLogin (admin test player flow)
 *  - URL has ?mode=session-link         → SessionLinkLogin (all envs)
 *  - URL has ?mode=moderator-join       → ModeratorJoinLogin
 *  - URL has ?mode=student-join         → StudentJoinLogin (real student joining a scheduled session from the portal)
 *  - URL has ?mode=test-session         → TestSessionLogin
 *  - dev build + ?mode=ui-gallery       → UiGallery (every UI primitive, no session)
 *  - no way in named                    → GuestLandingPage
 */

import { lazy, Suspense, useMemo } from 'react';
import { AdminLaunchLogin } from './admin-launch-login';
import { SessionLinkLogin } from './session-link-login';
import { TestSessionLogin } from './test-session-login';
import { GuestLandingPage } from './guest-landing-page';
import { ModeratorJoinLogin } from './moderator-join-login';
import { StudentJoinLogin } from './student-join-login';
import { SessionEndedEntry } from './session-ended-entry';
import { linkMode, pageSearch } from '../../domains/entry-links';
import { useSessionEndedStore } from '../../domains/session-ended';

// The primitives gallery, for development only. The lazy import is created
// inside the DEV branch so a production build drops it entirely: this file is
// in the eager chunk, and a static import would keep the gallery's CSS modules
// in it, because Rollup treats a CSS import as a side effect.
const UiGallery = import.meta.env.DEV
  ? lazy(() => import('../ui-gallery').then((m) => ({ default: m.UiGallery })))
  : null;

export function LoginScreen() {
  // The page's link, read once: the way in it names (domains/entry-links.ts).
  const search = useMemo(() => pageSearch(), []);
  const mode = linkMode(search);
  const sessionEnded = useSessionEndedStore((s) => s.left);

  // ── Routing (all hooks have been called above) ────────────────────────────

  if (mode === 'ui-gallery' && UiGallery) {
    return (
      <Suspense fallback={null}>
        <UiGallery />
      </Suspense>
    );
  }

  // Left a session the server ended, or reloaded a link whose end the tab
  // remembers: say so, and join nothing — a student's link would join the
  // ended session again by itself, and be refused.
  if (sessionEnded) {
    return <SessionEndedEntry search={search} />;
  }

  switch (mode) {
    case 'admin-launch':
      return <AdminLaunchLogin search={search} />;
    case 'session-link':
      return <SessionLinkLogin search={search} />;
    case 'moderator-join':
      return <ModeratorJoinLogin search={search} />;
    case 'student-join':
      return <StudentJoinLogin search={search} />;
    case 'test-session':
      return <TestSessionLogin search={search} />;
    default:
      return <GuestLandingPage search={search} />;
  }
}
